import React, { useCallback, useEffect, useMemo, useState } from "react";

import { strategiesStoresLoadedSelector } from "cortex/ui/src/fleet/redux-store/strategies/selectors";
import { playerVersionsOptionsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/player-versions/selectors";
import { tagsOptionsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/tags/selectors";
import DeviceDetailsDrawer from "cortex/ui/src/fleet/view/device/component/DeviceDetailsDrawer";
import DeviceTable from "cortex/ui/src/fleet/view/device/component/DeviceTable";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { compareVersion } from "cortex/ui/src/fleet/view/device/utils";
import { useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import { getAuthUser } from "cortex/ui/src/redux-store/auth";
import { deviceStoresLoadedSelector } from "cortex/ui/src/redux-store/devices/selectors";
import type { LastSeenDeviceCounts } from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks";
import { isVenueSyncEnabled } from "cortex/ui/src/redux-store/users";
import { Provider } from "cortex/ui/src/ui/fetch-options";
import { Option } from "cortex/ui/src/ui/table/types";
import { sortByLabelAsc } from "cortex/ui/src/util/sortByProp";

interface Prefiltered {
  network?: number;
  tags?: string[];
}

interface FullDeviceListTableProps {
  deviceStatsCounts?: LastSeenDeviceCounts;
  deviceMetricsCounts?: {
    upgradeAvailable: number;
    pendingUpgrade: number;
    pendingDowngrade: number;
    pendingVersionChange: number;
    neverConnected: number;
  };
  networkFilter?: boolean;
  prefiltered?: Prefiltered;
  storageKey?: string;
}

interface State {
  drawerOpen: boolean;
  networkOptions: Option[];
  selectedDevice: Device | null;
}

function getFilterOptions(options: ReadonlyArray<Option>) {
  return options.map(filterOptionMapper);
}

function filterOptionMapper({ label, value }: Option) {
  return {
    label,
    value: String(value),
  };
}

function getOptions(idValObject: Record<string, string>): Option[] {
  return Object.entries(idValObject)
    .map(([id, value]) => filterOptionMapper({ label: value, value: id }))
    .sort(sortByLabelAsc as any);
}

function computeNetworkOptions(networks?: Record<string, Network>): Option[] {
  if (!networks) return [];

  const idToName = Object.values(networks).reduce<Record<string, string>>(
    (acc, network: Network) => {
      acc[String(network.id)] = network.name;
      return acc;
    },
    {},
  );

  return getOptions(idToName);
}

function FullDeviceListTable({
  deviceStatsCounts,
  deviceMetricsCounts,
  networkFilter = true,
  prefiltered,
  storageKey,
}: FullDeviceListTableProps) {
  const devicesStoresLoaded = useAppSelector(deviceStoresLoadedSelector);
  const networks = useAppSelector(networksSelector);
  const networksStoresLoaded = useAppSelector(networksStoresLoadedSelector);
  const strategiesLoaded = useAppSelector(strategiesStoresLoadedSelector);
  const versionOptionRaw = useAppSelector(
    (state): ReadonlyArray<Option> =>
      getFilterOptions(playerVersionsOptionsSelector(state)),
  );
  const versionOptions = useMemo(
    () =>
      [...versionOptionRaw]
        .map((option) => ({ label: option.label, value: String(option.value) }))
        .sort((a, b) => compareVersion(b.label, a.label)),
    [versionOptionRaw],
  );
  const availableTags = useAppSelector(
    (state): ReadonlyArray<Option> =>
      getFilterOptions(tagsOptionsSelector(state)),
  );
  const tagsOptions = useMemo(
    () =>
      availableTags.filter((option: Option) => !option.label?.startsWith("_")),
    [availableTags],
  );
  const user = useAppSelector(getAuthUser);
  const venueSyncEnabled = useMemo(() => isVenueSyncEnabled(user), [user]);
  const storesLoaded = useMemo(
    () => devicesStoresLoaded && networksStoresLoaded && strategiesLoaded,
    [devicesStoresLoaded, networksStoresLoaded, strategiesLoaded],
  );

  const [state, setState] = useState<State>(() => ({
    drawerOpen: false,
    networkOptions: computeNetworkOptions(networks),
    selectedDevice: null,
  }));

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      networkOptions: computeNetworkOptions(networks),
    }));
  }, [networks]);

  const closeDeviceDetailsDrawer = useCallback(() => {
    setState((prev) => ({
      ...prev,
      drawerOpen: false,
      selectedDevice: null,
    }));
  }, []);

  const showDeviceDetailsDrawer = useCallback(
    (row: Device, isSelected: boolean, _event?: any) => {
      setState((prev) => ({
        ...prev,
        drawerOpen: isSelected,
        selectedDevice: row,
      }));
    },
    [],
  );

  const drawerTitle = useMemo(() => {
    return state.selectedDevice?.name || "Device Details";
  }, [state.selectedDevice]);

  return (
    <>
      <Provider.TagOptions />
      <DeviceTable
        closeDrawer={closeDeviceDetailsDrawer}
        deviceMetricsCounts={deviceMetricsCounts}
        deviceStatsCounts={deviceStatsCounts}
        networkFilter={networkFilter}
        networkOptions={state.networkOptions}
        onRowClick={showDeviceDetailsDrawer}
        prefiltered={prefiltered}
        storageKey={storageKey}
        storesLoaded={storesLoaded}
        tagsOptions={tagsOptions}
        venueSyncEnabled={venueSyncEnabled}
        versionOptions={versionOptions}
      />
      <DeviceDetailsDrawer
        device={state.selectedDevice ?? ({} as Device)}
        onClose={closeDeviceDetailsDrawer}
        open={state.drawerOpen}
        title={drawerTitle}
      />
    </>
  );
}

export { FullDeviceListTable };
export default FullDeviceListTable;
